import {
  APPRAISIFY_ADDRESS,
  APPRAISIFY_NAME,
  AloftDate,
  PropertyData,
  TKV2_ORDER_ATTACHMENT_API,
  getDatasetForOrder,
  schFilledAddress,
} from '@aloft/toolkit-shared';

import {
  fetchGetSubjectData,
  useCreateCompData,
  usePostTkv2XmlAttachmentAPI,
} from '../../../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../../../../hooks/api/uploadBlob.ts';
import { useAutofillProperty } from '../../../../../hooks/useAutofillProperty.ts';
import { useCreateOrder } from '../../../hooks/useCreateOrder.ts';
import { CraftsmanCreateOrderForm } from '../types.ts';
import { useAnalyzeSubjectImages } from './useAnalyzeSubjectImages.ts';
import { useUpdateInitialSubjectValues } from './useUpdateInitialSubjectValues.ts';
import { useUpdateOrderFeatures } from './useUpdateOrderFeatures.ts';

export const useCreateCraftsmanOrder = (): ((
  form: CraftsmanCreateOrderForm,
) => Promise<string>) => {
  const createOrder = useCreateOrder();
  const createCompData = useCreateCompData();
  const autofillProperty = useAutofillProperty();
  const analyzeSubject = useAnalyzeSubjectImages();
  const createXmlAttachment = usePostTkv2XmlAttachmentAPI();
  const updateInitialSubject = useUpdateInitialSubjectValues();
  const updateOrderFeatures = useUpdateOrderFeatures();
  const ctx = useToolkitContext();
  const { fetcherOptions } = ctx;

  return async (payload) => {
    let blobId;
    const xml = payload.imported?.xml;
    if (xml) {
      const blobRes = await uploadBlob(xml, xml.name, ctx);
      blobId = blobRes.id;
    }

    const craftsmanDatasetVersion = getDatasetForOrder({
      effectiveDate: payload.effectiveDate,
      creationDate: AloftDate.today(),
    });

    const inspectifyPayload =
      payload.inspectifyOrder && !xml
        ? {
            appraisalType: payload.appraisalType,
            clientName: payload.clientName,
            dueDate: payload.dueDate,
            inspectionDate: payload.inspectionDate,
            appraiserInstructions: payload.appraiserInstructions,
            intendedUsers: payload.clientName,
            intendedUse: payload.intendedUse,
            inspectorType: payload.inspectorType,
            borrowerName: payload.borrowerName,
            inspectionData: { hasInspection: payload.appraisalType === 'Hybrid Appraisal', reportId: payload.reportId },
            options: payload.options,
            appraisifyName: APPRAISIFY_NAME,
            appraisifyAddress: APPRAISIFY_ADDRESS,
            mock: payload.mock,
          }
        : {
            appraisalType: undefined,
            clientName: undefined,
            dueDate: undefined,
            inspectionDate: undefined,
            appraiserInstructions: undefined,
            intendedUsers: undefined,
            intendedUse: undefined,
            inspectorType: undefined,
            borrowerName: undefined,
            inspectionData: undefined,
            options: payload.options,
            mock: false,
          };

    // Step 1: Create order
    const order = await createOrder(
      {
        ...payload,
        version: 2,
        isCraftsman: true,
        craftsmanAdjustmentVersion: '1.0',
        craftsmanCalculatorVersion: '1.0',
        craftsmanDatasetVersion,
        address: payload.address,
        ...inspectifyPayload,
      },
      undefined,
      payload.imported?.subject,
    );

    if (blobId) {
      await createXmlAttachment.mutateAsync({
        pathParams: { orderID: order.id },
        body: { id: blobId },
      });
    }

    // Step 2A: Run subject image analysis
    const subjectImageAnalysisPromise =
      payload.propertyType === 'singleFamilyResidence' ? analyzeSubject(order.id) : undefined;
    const subjectPromise = fetchGetSubjectData({
      ...fetcherOptions,
      pathParams: { orderID: order.id },
    }).then((res) => TKV2_ORDER_ATTACHMENT_API.SUBJECT_DATA.GET.RESPONSE.parse(res));

    // Step 2B: Create comps
    const compPromises: Promise<PropertyData>[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const comp of payload.imported?.comps ?? []) {
      // Wait 2 seconds between requests to reduce chance of throttling
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });

      if (comp.address?.AddressLineText) {
        compPromises.push(
          autofillProperty({
            address: schFilledAddress.parse(comp.address),
            xmlProperty: comp,
            effectiveYear: payload.effectiveDate.getYear(),
            propertyType: comp.propertyType,
            orderID: order.id,
          }),
        );
      }
    }
    const [subjectImageAnalysis, subject, ...comps] = await Promise.all([
      subjectImageAnalysisPromise,
      subjectPromise,
      ...compPromises,
    ]);

    // Step 3A: Run image analysis on the subject to get initial craftsman quality
    const subjectQuality = subjectImageAnalysis?.body.result.overall_class;

    // Step 3B: Create comps
    const compsUpdatePromise = createCompData.mutateAsync({
      pathParams: { orderID: order.id },
      body: TKV2_ORDER_ATTACHMENT_API.COMP_DATA.POST.REQUEST.BODY.parse({
        comps,
      }),
    });

    // Step 3C: Update subject with initial craftsman quality value
    const subjectUpdatePromise = updateInitialSubject(
      order.id,
      subject.editableData,
      subjectQuality ?? subject.editableData.improvements.craftsmanQuality,
    );

    await Promise.all([compsUpdatePromise, subjectUpdatePromise]);

    // Step 4: Update the order features based on the subject and comps' features
    await updateOrderFeatures(order.id);

    return order.id;
  };
};
